import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { DataService } from "./data.service";
import { AddActivityNotePayload } from "../models/activity-notes.model";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";
import { AppointmentChecklist } from "../models/admissions.model";
import { ApplicationCacheService } from "./application-cache.service";
import { ApiStatus, ReferralApis } from "./utils";
import {
  normalizeActivityNotesList,
} from "../helpers/activity-note-nurse-note.util";
import {
  ActivityEncounterSource,
  enrichActivityNotePayloadWithEncounterContext,
} from "../helpers/activity-encounter-context.util";
import { ActivityNote } from "../models/activity-notes.model";

@Injectable({
  providedIn: "root",
})
export class ActivityNotesService {
  constructor(
    private readonly _dataService: DataService,
    private readonly _applicationCacheService: ApplicationCacheService
  ) {}

  getPatientActivitiesByProcedureId(
    patientId: string,
    procedureId: string,
    options?: { isNurseNote?: boolean }
  ): Observable<any[]> {
    const pid = String(patientId || "").trim();
    const procId = String(procedureId || "").trim();
    const url = `${this.apiBaseUrl}api/v1/activities/patient/${pid}/procedure/${procId}`;
    const nurseNoteFilter = options?.isNurseNote;
    const requestOptions =
      nurseNoteFilter === true || nurseNoteFilter === false
        ? {
            params: new HttpParams().set(
              "isNurseNote",
              nurseNoteFilter ? "true" : "false"
            ),
          }
        : undefined;

    return this._dataService.get(url, requestOptions).pipe(
      map((response) => normalizeActivityNotesList(response))
    );
  }

  getPatientActivitiesByPatientId(
    patientId: string,
    options?: { isNurseNote?: boolean }
  ): Observable<any[]> {
    const pid = String(patientId || "").trim();
    const url = `${this.apiBaseUrl}api/v1/activities/patient/${pid}`;
    const nurseNoteFilter = options?.isNurseNote;
    const requestOptions =
      nurseNoteFilter === true || nurseNoteFilter === false
        ? {
            params: new HttpParams().set(
              "isNurseNote",
              nurseNoteFilter ? "true" : "false"
            ),
          }
        : undefined;

    return this._dataService.get(url, requestOptions).pipe(
      map((response) => normalizeActivityNotesList(response))
    );
  }

  addNewActivityNote(payload: AddActivityNotePayload) {
    const enriched = this.enrichPayloadWithEncounterContext(payload);
    if (
      enriched.requireEncounterAssociation === true &&
      !String(enriched.encounterId ?? "").trim()
    ) {
      return throwError(
        () =>
          new Error(
            "encounterId is required when recording this activity on schedule, encounter, or procedure room"
          )
      );
    }
    const url = this.buildActivityNoteUrl(enriched);
    return this._dataService.post(url, enriched).pipe(
      map((note) => this.normalizeSavedActivityNote(note, enriched))
    );
  }

  updateActivityNote(id: string, payload: AddActivityNotePayload) {
    const enriched = this.enrichPayloadWithEncounterContext(payload);
    const url = this.buildActivityNoteUrl(enriched, id);
    return this._dataService.patch(url, enriched).pipe(
      map((note) => this.normalizeSavedActivityNote(note, enriched))
    );
  }

  private buildActivityNoteUrl(
    payload: AddActivityNotePayload,
    id?: string
  ): string {
    const base = id
      ? `${this.apiBaseUrl}api/v1/activities/${id}`
      : `${this.apiBaseUrl}api/v1/activities`;
    if (payload.isNurseNote === true) {
      return `${base}?isNurseNote=true`;
    }
    if (payload.isNurseNote === false) {
      return `${base}?isNurseNote=false`;
    }
    return base;
  }

  /** API create/update responses often omit `isNurseNote` even when the row is a nurse note. */
  private normalizeSavedActivityNote(
    note: ActivityNote,
    payload: AddActivityNotePayload
  ): ActivityNote {
    if (!note || typeof note !== "object") {
      return note;
    }
    const payloadNotes = String(payload.notes ?? payload.comment ?? "").trim();
    let normalized: ActivityNote = {
      ...note,
      isNurseNote: payload.isNurseNote === true,
      ...(payloadNotes && !String(note.notes ?? "").trim()
        ? { notes: payloadNotes }
        : {}),
      ...(payload.activityType && !String(note.activityType ?? "").trim()
        ? { activityType: payload.activityType }
        : {}),
      ...(payload.activityBy && !String(note.activityBy ?? "").trim()
        ? { activityBy: payload.activityBy }
        : {}),
    };
    if (payload.createdAt) {
      normalized = { ...normalized, createdAt: payload.createdAt };
    }
    if (payload.encounterId && !String(normalized.encounterId ?? "").trim()) {
      normalized = { ...normalized, encounterId: payload.encounterId };
    }
    if (payload.selectedDate && !String(normalized.selectedDate ?? "").trim()) {
      normalized = { ...normalized, selectedDate: payload.selectedDate };
    }
    return normalized;
  }

  private enrichPayloadWithEncounterContext(
    payload: AddActivityNotePayload
  ): AddActivityNotePayload {
    return enrichActivityNotePayloadWithEncounterContext(
      payload,
      this.activityEncounterSourceFromCache(),
      {
        requireEncounterAssociation:
          payload.requireEncounterAssociation === true,
      }
    );
  }

  private activityEncounterSourceFromCache(): ActivityEncounterSource {
    const cache = this._applicationCacheService.applicationCache;
    return {
      procedureListing: cache?.procedureListing,
      appointment: cache?.appointment,
      procedure: cache?.procedure,
    };
  }

  deleteActivityNoteById(id: string) {
    const url = `${this.apiBaseUrl}api/v1/activities/${id}`;
    return this._dataService.delete(url);
  }

  getEncounterCheckList(
    appointmentId: string
  ): Observable<AppointmentChecklist> {
    const url = `${this.apiBaseUrl}api/v1/encounters/${appointmentId}/checklist`;
    return this._dataService.get(url).pipe(
      catchError((error) => {
this._applicationCacheService.setApiStatus(
          ApiStatus.Failed,
          ReferralApis.ChecklistFlags
        );
        return throwError(() => error);
      }),
      tap((checklist: AppointmentChecklist) => {
        this._applicationCacheService.updateAppointmentChecklistFlags(
          checklist
        );
        this._applicationCacheService.setApiStatus(
          ApiStatus.Success,
          ReferralApis.ChecklistFlags
        );
      })
    );
  }

  private get apiBaseUrl(): string {
    return environment.apiBaseUrl;
  }
}
